import { useEffect, useMemo, useState } from "react";
import { fetchLiveArticle, type LiveArticleDto } from "../api";
import { ResearchMatrixTable, type MatrixColumn, type RowHighlightConfig } from "../components/ResearchMatrixTable";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../indexRotation.css";

const SLUG = "index-rotation";
const CANONICAL = "https://tickerpicker.in/index-rotation";

interface Verdicts {
  rank_persistence: { value: number; p_value: number };
  extreme_reversion: {
    "rank_1_2 (best)": { n: number; diff: number; p_value: number; cond_mean: number; overall_mean: number } | null;
    "rank_16_17 (worst)": { n: number; diff: number; p_value: number; cond_mean: number; overall_mean: number } | null;
  };
  transition_matrix: { chi_sq_like_stat: number; p_value: number };
  exploitability: { rule: string; n_weeks: number; rule_mean_weekly_ret: number; equal_weight_mean_weekly_ret: number; diff: number; p_value: number };
}

interface RotationResult {
  n_weeks: number;
  n_sectors: number;
  n_perm: number;
  date_range: { start: string; end: string };
  tickers: string[];
  rows: Record<string, unknown>[];
  verdicts: Verdicts;
}

function pct(v: number, decimals = 2): string {
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(decimals)}%`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

export function IndexRotationPage() {
  const [data, setData] = useState<LiveArticleDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Humanized-voice rewrite signed off 2026-10-28 (reviewed on an
  // isolated preview build, flipped together with the rest of the Market
  // Myths batch). Live data fetching, the transposed-table computation,
  // row-highlight config, and the live "example" computation are
  // unchanged from the original build -- only the hardcoded English copy
  // changed register. SENSEX/NSE taxonomy corrected in the same flip:
  // SENSEX is a BSE index, never NSE.

  useEffect(() => {
    fetchLiveArticle(SLUG)
      .then(setData)
      .catch(() => setError("Could not load this article's live data."));
  }, []);

  const result = data?.result as unknown as RotationResult | undefined;
  const n = result?.n_sectors ?? 15;

  useSeoTags({
    title: "Every sector takes its turn — except the pattern isn't really there — Tickerpicker",
    description: "A living, weekly-recomputed test of whether NSE's sector indices take predictable turns at " +
      "the top of the rankings. Explore the real weekly rank history yourself, then see the statistical test " +
      "run against exactly the pattern you'll probably think you found.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: result
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Every sector takes its turn — except the pattern isn't really there",
          description: `A living, weekly-recomputed rank-rotation test across ${result.n_sectors} NSE sector indices.`,
          url: CANONICAL,
          publisher: { "@type": "Organization", name: "Tickerpicker" },
          isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
        }
      : undefined,
  });

  const transposedColumns: MatrixColumn[] | null = result
    ? [
        { key: "ticker", label: "Sector" },
        ...[...result.rows].reverse().map((r) => ({ key: String(r.week), label: String(r.week) })),
      ]
    : null;

  const transposedRows: Record<string, unknown>[] | null = result
    ? result.tickers.map((t) => {
        const row: Record<string, unknown> = { ticker: t };
        for (const r of result.rows) row[String(r.week)] = r[t];
        return row;
      })
    : null;

  const rowHighlight: RowHighlightConfig | null = result
    ? {
        presets: [
          { key: "top1", label: "Ranked #1", test: (v) => v === 1 },
          { key: "topq", label: `Top quartile (rank ≤${Math.ceil(n / 4)} of ${n})`, test: (v) => typeof v === "number" && v <= Math.ceil(n / 4) },
        ],
        rowOptions: result.tickers.map((t) => ({ key: t, label: t })),
        gapsReadoutEnabled: true,
      }
    : null;

  const example = useMemo(() => {
    if (!result) return null;
    let bestTicker: string | null = null;
    let bestHits: number[] = [];
    for (const t of result.tickers) {
      const hits: number[] = [];
      result.rows.forEach((r, i) => {
        if (r[t] === 1) hits.push(i);
      });
      if (!bestTicker || hits.length > bestHits.length) {
        bestTicker = t;
        bestHits = hits;
      }
    }
    if (!bestTicker || bestHits.length < 2) return null;
    const gaps = bestHits.slice(1).map((h, i) => h - bestHits[i]);
    const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    return { ticker: bestTicker, count: bestHits.length, gaps, mean };
  }, [result]);

  const v = result?.verdicts;
  const best = v?.extreme_reversion["rank_1_2 (best)"];
  const worst = v?.extreme_reversion["rank_16_17 (worst)"];

  return (
    <div className="mm-shell">
      <nav className="mm-nav">
        <a href="/" className="mm-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <a href="/market-myths">Market Myths</a>
        </div>
      </nav>

      <article className="mm-evidence ir-article">
        <div className="mm-kick">Market Myths: Tested — living article</div>
        <h1 className="mm-evidence-title">Every sector takes its turn — except the pattern isn't really there</h1>

        <PageHelper>
          This page recomputes itself every week against the real, growing database, so the verdict you're
          reading is current, not a snapshot from whenever we happened to write it. If enough new weeks ever
          flip it, this page will show the flip.
        </PageHelper>

        {error && <div className="mm-error">{error}</div>}
        {!data && !error && <div className="mm-loading">Loading…</div>}

        {data && result && (
          <>
            {data.last_run_failed && (
              <div className="ir-banner ir-banner-error">
                The most recent weekly recompute failed — showing the last successful result instead.
              </div>
            )}
            {data.stale && !data.last_run_failed && (
              <div className="ir-banner ir-banner-stale">
                This result is older than its {data.cadence_days}-day refresh cadence — a recompute is overdue
                but hasn't landed yet. Showing the last successful result.
              </div>
            )}
            <div className="ir-asof-badge">
              <span className="ir-asof-dot" />
              <b>Live — recomputed weekly from real data.</b> As of {fmtDate(data.computed_at)} ·{" "}
              {result.n_weeks} weeks ({fmtDate(result.date_range.start)} – {fmtDate(result.date_range.end)}) ·
              {" "}{n} NSE sector indices
            </div>

            <p className="ir-invite">
              Every week, NSE's {n} major <b>sector</b> indices get re-ranked 1–{n} by that week's return — 1 is
              the best performer, {n} the worst. NIFTY and SENSEX sit this one out; they're broad,
              whole-market benchmarks, not sectors that could be said to "take a turn." Below is the real, full
              weekly rank history for all {n} — one row per sector, one column per week, most recent first,
              nothing smoothed. Pick a sector and highlight its <b>#1</b> or <b>top-quartile</b> weeks along its
              own row, look at the gaps between them, or click a week's column to sort everyone by that week's
              rank. Most people who try this spot what looks like a rhythm — a sector that seems to return to
              the top every few months. <b>See if you can find one.</b>
            </p>

            <ResearchMatrixTable
              rows={transposedRows!}
              columns={transposedColumns!}
              rowKey={(r) => String(r.ticker)}
              rowHighlight={rowHighlight!}
              exportFilename={`index_rotation_weekly_ranks_${result.date_range.end}.csv`}
            />

            <h2 className="ir-h2">Now the test</h2>
            <p className="ir-p">
              Whatever rhythm you just spotted, here's the actual test of it, run fresh on the same data every
              week. Each result is measured against a <FieldHelp term="shuffled_week_null" />{" "}
              <b>shuffled-week null</b>: a thousand random reshuffles of which week follows which, keeping every
              week's own rank set intact. A real p-value is simply how often that kind of random reshuffle alone
              produces a pattern this strong.
            </p>

            <div className="ir-verdicts">
              <div className="ir-vcard">
                <div className="ir-vlabel">
                  <FieldHelp term="rotation_rank_persistence" /> Rank persistence
                </div>
                <div className="ir-vval">{v!.rank_persistence.value >= 0 ? "+" : ""}{v!.rank_persistence.value.toFixed(4)}</div>
                <div className="ir-vp">p = {v!.rank_persistence.p_value.toFixed(3)}</div>
                <div className="ir-vread">Not distinguishable from random week-reshuffling.</div>
              </div>

              <div className="ir-vcard">
                <div className="ir-vlabel">Extreme-rank reversion (best)</div>
                <div className="ir-vval">{best ? pct(best.diff) : "—"}</div>
                <div className="ir-vp">{best ? `p = ${best.p_value.toFixed(3)} (n=${best.n})` : ""}</div>
                <div className="ir-vread">This week's #1/#2's OWN next-week return, vs the unconditional average.</div>
              </div>

              <div className="ir-vcard">
                <div className="ir-vlabel">Extreme-rank reversion (worst)</div>
                <div className="ir-vval">{worst ? pct(worst.diff) : "—"}</div>
                <div className="ir-vp">{worst ? `p = ${worst.p_value.toFixed(3)} (n=${worst.n})` : ""}</div>
                <div className="ir-vread">This week's #{n - 1}/#{n}'s OWN next-week return, vs the unconditional average.</div>
              </div>

              <div className="ir-vcard">
                <div className="ir-vlabel">Quartile transition structure</div>
                <div className="ir-vval">{v!.transition_matrix.chi_sq_like_stat.toFixed(1)}</div>
                <div className="ir-vp">p = {v!.transition_matrix.p_value.toFixed(3)}</div>
                <div className="ir-vread">Association strength of which rank-quartile follows which, vs random.</div>
              </div>

              <div className="ir-vcard ir-vcard-wide">
                <div className="ir-vlabel"><FieldHelp term="oos_exploitability" /> Out-of-sample exploitability</div>
                <div className="ir-vval">
                  {pct(v!.exploitability.diff, 2)}/wk <span className="ir-vrule">({v!.exploitability.rule.replace("_", " ")})</span>
                </div>
                <div className="ir-vp">p = {v!.exploitability.p_value.toFixed(3)} ({v!.exploitability.n_weeks} held-out weeks)</div>
                <div className="ir-vread">
                  Whichever rule looked best on the first 70% of history, tested unchanged on the held-out last
                  30%, vs. simply holding the equal-weight basket every week.
                </div>
              </div>
            </div>

            <p className="ir-reconcile">
              Current as of {fmtDate(data.computed_at)}, updating weekly, on {n} SECTOR indices only. The linked
              paper's Investigation 4 tested all 17 Indian market indices tracked by this project — NIFTY, the 15
              NSE sector indices, and BSE SENSEX — with NIFTY and SENSEX included only as broad benchmarks; this
              page narrows that to a genuine sector-rotation question. Same method, same shuffled-week-null
              discipline, narrower universe, <b>same null verdict</b>.
            </p>

            <div className="mm-verdict mm-v-bust ir-finalverdict">BUSTED — no validated sector-rotation edge</div>

            {example && (
              <div className="ir-story">
                <h2 className="ir-h2">What happened when we actually looked</h2>
                <p className="ir-p">
                  You probably highlighted a sector above and watched it light up every few weeks — that's not
                  an illusion, it's real data. <b>{example.ticker}</b> finished #1 more often than anyone else
                  in this history: <b>{example.count} separate weeks</b>. Laid end to end, the gaps between
                  those finishes look almost regular at a glance — <b>mean {example.mean.toFixed(1)} weeks</b>{" "}
                  apart. That "every ~{Math.round(example.mean)} weeks" framing is exactly the kind of thing
                  that reads as a cycle.
                </p>
                <p className="ir-p">
                  The actual gap sequence, though: <span className="ir-gapseq">{example.gaps.slice(0, 20).join(", ")}
                  {example.gaps.length > 20 ? ", …" : ""}</span>. A real cycle clusters tightly around its
                  average; these gaps range from {Math.min(...example.gaps)} to {Math.max(...example.gaps)}{" "}
                  weeks. The mean is real. The rhythm it implies isn't — a single average number flattens wildly
                  irregular spacing into something that looks like clockwork. It's the same trap the eye falls
                  into scanning the table above, and it's why every test below gets compared against what pure
                  randomness produces, not against intuition.
                </p>

                <p className="ir-p">
                  Here's what each test actually says:
                </p>
                <ul className="ir-storylist">
                  <li>
                    <b>Rank persistence ({v!.rank_persistence.value >= 0 ? "+" : ""}{v!.rank_persistence.value.toFixed(4)}, p = {v!.rank_persistence.p_value.toFixed(3)}):</b>{" "}
                    does last week's leader tend to stay near the top? The real correlation is tiny, and random
                    week-reshuffling alone produces a correlation this size roughly{" "}
                    {Math.round(v!.rank_persistence.p_value * 100)}% of the time — far too often to call real.
                  </li>
                  <li>
                    <b>Extreme-rank reversion (p = {best?.p_value.toFixed(3)} best, {worst?.p_value.toFixed(3)} worst):</b>{" "}
                    does this week's #1 (or #{n}) tend to keep winning (or losing) next week? Both forward
                    returns are statistically indistinguishable from the sector-wide average — whatever a
                    sector did this week carries essentially no information about next week.
                  </li>
                  <li>
                    <b>Quartile transition structure ({v!.transition_matrix.chi_sq_like_stat.toFixed(1)}, p = {v!.transition_matrix.p_value.toFixed(3)}):</b>{" "}
                    does which performance quartile a sector sits in predict which quartile it moves to next
                    week? Random reshuffling produces structure this strong{" "}
                    {Math.round(v!.transition_matrix.p_value * 100)}% of the time — not rare enough to trust.
                  </li>
                  <li>
                    <b>Out-of-sample exploitability ({pct(v!.exploitability.diff, 2)}/wk, p = {v!.exploitability.p_value.toFixed(3)}):</b>{" "}
                    the single most generous test — pick whichever rule looked best on the first 70% of
                    history, then actually trade it, unmodified, on the held-out last 30%. It barely
                    outperforms, and underperforms about as often as a coin flip would, simply holding the
                    equal-weight basket of all {n} sectors every week.
                  </li>
                </ul>

                <p className="ir-p">
                  <FieldHelp term="shuffled_week_null" /> Why random reshuffling is the right yardstick: imagine
                  writing each week's complete set of {n} sector rankings on its own card, shuffling the whole
                  deck, and dealing it back out in a new random order. That destroys any real week-to-week
                  relationship while keeping every individual week's rankings exactly as they happened. A
                  p-value is how often that reshuffle, repeated a thousand times, produces a pattern as strong
                  as the one we actually measured. When that happens often — as it does in every test above —
                  the real pattern isn't distinguishable from noise, however compelling it looked in the table.
                </p>

                <p className="ir-p">
                  Markets do repeat some things — sector rotation is a real, well-documented phenomenon
                  elsewhere, and illiquid stocks in this project's own research show genuine short-horizon
                  structure. This specific claim — that NSE's major sector indices take their turn at the top on
                  a predictable schedule — just doesn't survive being tested against real randomness, on this
                  data, at this frequency. Eyeballing a table isn't a test. A test compares what you see against
                  what pure chance alone would produce, and reports the result even when the pattern looked
                  compelling a moment ago.
                </p>
              </div>
            )}

            <p className="ir-p">
              This reuses the same rank-rotation method (and the same shuffled-week-null discipline) developed
              for the broader index-direction investigation — see{" "}
              <a href="/market-myths?evidence=index-direction">the full methodology</a> for the method in depth
              and the other three tests run alongside it.
            </p>
          </>
        )}

        <div className="mm-footnote">
          This page recomputes automatically on a weekly cadence against the live database — the numbers above
          are never a frozen one-off snapshot. TickerPicker is a personal, non-commercial research project.
          Nothing here is investment advice.
        </div>
      </article>
    </div>
  );
}
